/**
 * What the player shows of an `<audio>` element (UC-8, light). The element knows the length of
 * a playback copy at once; of a recording as the browser's recorder wrote it, it may read
 * `Infinity` until it has been played through. Until it knows, the length the server measured
 * stands in. Pure.
 */
import type { PlaybackState } from '@/components/humint/questioning/questioning-types'
import type { RecordingData } from '@/components/humint/questioning/recording-types'
import { startInside } from './recording-path'

/** What is read of the element on each of its events. */
export interface AudioReadout {
  /** Seconds. */
  currentTime: number
  /** Seconds; `NaN` before the metadata, `Infinity` for a stream without a length. */
  duration: number
  paused: boolean
  ended: boolean
}

/** An element nothing was loaded into yet. */
export const IDLE_READOUT: AudioReadout = {
  currentTime: 0,
  duration: Number.NaN,
  paused: true,
  ended: false,
}

/**
 * The recording's length for the player until the element knows its own: what the server
 * measured, else where the last turn ends.
 */
export function recordingLengthMs(
  recording: Pick<RecordingData, 'durationMs' | 'transcript'>
): number {
  const lastTurn = recording.transcript.turns[recording.transcript.turns.length - 1]
  return recording.durationMs ?? lastTurn?.endMs ?? 0
}

/** The element's own length once it knows one, else the fallback. */
export function playbackDurationMs(elementSeconds: number, fallbackMs: number): number {
  const known = Number.isFinite(elementSeconds) && elementSeconds > 0
  return known ? Math.round(elementSeconds * 1000) : Math.max(0, fallbackMs)
}

/** The player's state; a position past a length that was only assumed stretches the length. */
export function toPlaybackState(readout: AudioReadout, fallbackDurationMs: number): PlaybackState {
  const positionMs = Math.max(0, Math.round(readout.currentTime * 1000))
  return {
    positionMs,
    durationMs: Math.max(positionMs, playbackDurationMs(readout.duration, fallbackDurationMs)),
    playing: !readout.paused && !readout.ended,
  }
}

/**
 * Where an element that has just read its metadata is put, in seconds: the time the page was
 * opened at (`?t=`). Null leaves it at the beginning: no time was asked for, or one past the
 * element's own length — the address is anybody's to edit. An element that does not know its
 * length yet (`NaN`, `Infinity`) is sought all the same.
 */
export function startSecondsFor(
  startMs: number | null | undefined,
  elementSeconds: number
): number | null {
  if (startMs == null || startMs <= 0) return null
  const known = Number.isFinite(elementSeconds) && elementSeconds > 0
  return startInside(startMs / 1000, known ? elementSeconds : 0)
}

/**
 * Where an element that already holds the recording goes when the time asked for changes —
 * another link to the same recording — in seconds. Here the beginning is a time like any other:
 * `?t=0`, the link of a passage that starts at 00:00, takes the player back to it. Null leaves
 * the element where it is: the address names no time any more, or one that is none of this
 * recording.
 */
export function seekSecondsFor(
  startMs: number | null | undefined,
  elementSeconds: number
): number | null {
  return startMs === 0 ? 0 : startSecondsFor(startMs, elementSeconds)
}

/**
 * What is read of an element nothing was loaded into yet, standing at the time asked for: the
 * scrubber and the marked turn show where playback will start before the audio is here.
 */
export function readoutAt(startMs: number | null | undefined): AudioReadout {
  return startMs != null && startMs > 0
    ? { ...IDLE_READOUT, currentTime: startMs / 1000 }
    : IDLE_READOUT
}
